import React, {useEffect, useState} from 'react';
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {InputTextarea} from "primereact/inputtextarea";

import {AppContext} from "../../HomePage";

// Turns a campaign's search diagnostics into a prompt for an agent working in the
// pricescraper repo.
//
// The prompt hands over FACTS and delegates every judgement to the `scraper-dev` skill,
// which already knows this repo: how to tell whether a site is already handled, how to
// pick between extending a platform base and writing a standalone spider, how to verify
// locally, and how to register the site (`register_spider.py` into `price_xpath`) before
// deploying. Restating any of that here would create a second copy free to go stale, and
// a prompt that tells an agent HOW to do something the skill already specifies is a
// prompt that will one day contradict it.
//
// Deliberately NOT a "register this site" button. Writing `price_xpath` from the panel
// would be a production config write made by whoever happens to be reading a report,
// with no spider verified behind it. The skill's sequence - verify the spider, show the
// record for review, then register - exists precisely because that order matters.
const SpiderFixPromptDialog = ({visible, onHide, data, campaignId}) => {

    const {t} = React.useContext(AppContext);
    const label = (key) => t(`search_diagnostics.${key}`);

    const broken = data?.top_broken_hosts || [];
    const parsed = data?.parsed_hosts || [];

    const [selected, setSelected] = useState([]);
    const [copied, setCopied] = useState(false);

    // Preselect the broken hosts: they are what an admin opened this for. The working
    // hosts are there to be opted INTO, since registering a site is a smaller, different
    // job from fixing a spider.
    useEffect(() => {
        if (visible) {
            setSelected(broken.map(entry => entry.host));
            setCopied(false);
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (host) => setSelected(selected.includes(host)
        ? selected.filter(item => item !== host)
        : [...selected, host]);

    const outcomeName = (outcome) => t(`search_diagnostics.outcome_${outcome}`);

    const buildPrompt = () => {
        const chosenBroken = broken.filter(entry => selected.includes(entry.host));
        const chosenParsed = parsed.filter(entry => selected.includes(entry.host));
        const lines = [];

        lines.push('Use the `scraper-dev` skill in the pricescraper repo.');
        lines.push('');
        lines.push('The sites below came out of a competitor search' +
            (campaignId ? ` for campaign ${campaignId}` : '') +
            (data?.run?.started_at ? ` on ${new Date(data.run.started_at).toLocaleString()}` : '') +
            '. The counts say how many PRODUCTS each site cost or won, so they are a' +
            ' priority order, not just a list.');
        lines.push('');
        lines.push('Follow the skill\'s step 0 for every site before writing anything: a site may' +
            ' already be handled by a dedicated spider, by a base spider carrying it in' +
            ' key_urls, or by a `price_xpath` record naming a base spider.');

        if (chosenBroken.length) {
            lines.push('');
            lines.push('## Sites that failed');
            chosenBroken.forEach(entry => {
                lines.push('');
                lines.push(`### ${entry.host}`);
                lines.push(`- outcome: ${outcomeName(entry.reason)} ` +
                    `(${entry.products} products, ${entry.urls} URLs)`);
                if (entry.detail) lines.push(`- error: ${entry.detail}`);
                (entry.sample_urls || []).forEach(url => lines.push(`- ${url}`));
            });
        }

        if (chosenParsed.length) {
            lines.push('');
            lines.push('## Sites that were parsed, but may have no site definition');
            lines.push('');
            lines.push('Each of these was parsed by the spider named below during the search.' +
                ' Where that is a platform base spider and the host has no `price_xpath`' +
                ' record of its own, registering the site against that base is what turns a' +
                ' one-off parse into a supported site. Check first - some of these will' +
                ' already be registered.');
            chosenParsed.forEach(entry => {
                lines.push('');
                lines.push(`### ${entry.host}`);
                lines.push(`- parsed by: ${entry.spider} (${entry.products} products)`);
                (entry.sample_urls || []).forEach(url => lines.push(`- ${url}`));
            });
        }

        lines.push('');
        lines.push('Verify locally before deploying, and do not register anything in the' +
            ' database or deploy without showing me the change first.');
        return lines.join('\n');
    };

    const prompt = buildPrompt();

    const copy = () => {
        // execCommand is the fallback that matters here: navigator.clipboard is undefined
        // on a page served over plain HTTP, and a copy button that silently does nothing
        // is worse than no button.
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(prompt).then(done);
            return;
        }
        const area = document.createElement('textarea');
        area.value = prompt;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try { document.execCommand('copy'); done(); } finally { document.body.removeChild(area); }
    };

    const hostRow = (entry, suffix) => <div key={entry.host} style={{marginBottom: '.35em'}}>
        <Checkbox inputId={`fix-${entry.host}`} checked={selected.includes(entry.host)}
                  onChange={() => toggle(entry.host)}/>
        <label htmlFor={`fix-${entry.host}`} style={{marginLeft: '.5em', cursor: 'pointer'}}>
            {entry.host}
            <span style={{color: 'var(--text-color-secondary)', marginLeft: '.5em'}}>
                {suffix}
            </span>
        </label>
    </div>;

    return <Dialog header={label('fix_prompt')} visible={visible} onHide={onHide}
                   style={{width: '52rem', maxWidth: '95vw'}}>
        {broken.length > 0 && <>
            <h6 style={{marginBottom: '.5em'}}>{label('top_broken_hosts')}</h6>
            {broken.map(entry => hostRow(entry,
                `${outcomeName(entry.reason)} · ${entry.products} ${label('products_column')}`))}
        </>}

        {parsed.length > 0 && <>
            <h6 style={{marginTop: '1em', marginBottom: '.5em'}}>{label('parsed_hosts')}</h6>
            {parsed.map(entry => hostRow(entry,
                `${entry.spider} · ${entry.products} ${label('products_column')}`))}
        </>}

        <h6 style={{marginTop: '1.25em', marginBottom: '.5em'}}>{label('generated_prompt')}</h6>
        <InputTextarea value={prompt} rows={16} readOnly={true} autoResize={false}
                       style={{width: '100%', fontFamily: 'monospace', fontSize: '.85em'}}/>
        <div style={{marginTop: '.75em', textAlign: 'right'}}>
            <Button label={copied ? label('copied') : label('copy')}
                    icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                    onClick={copy} disabled={selected.length === 0}/>
        </div>
    </Dialog>;
};

export default SpiderFixPromptDialog;
